/**
 * Frontend entry point. Bundled by `npm run js` into assets/scripts/.
 *
 * As of step 5b this is the only script the site SHIPS -- the one exception is
 * Plausible, which the `analytics` snippet loads from plausible.io, and only on
 * the live host. jQuery, Popper,
 * Bootstrap, Isotope, imagesLoaded, Fancybox, the jQuery background video
 * plugin and AOS are gone, and with them roughly 430 KB of vendor JavaScript
 * and 170 KB of vendor CSS.
 *
 * Step 8 turned the direct calls into a registry, which is what the old
 * docblock said would happen and why: Swup replaces the containers, so the
 * modules have to be torn down before the markup goes and built again after.
 *
 * MODULES is an unordered set, and as of E46 that is true rather than merely
 * convenient. It used to carry one ordering constraint -- initHero subscribed
 * to the `tabshown` event initTabs fired while opening the first tab, so it
 * had to be registered first. Both modules are gone with the tab widget, and
 * with them the last edge between two entries here.
 *
 * A module returns a teardown only if it holds something that OUTLIVES its
 * markup -- a listener on the document or the window, an observer, a
 * PhotoSwipe instance. Listeners bound to elements inside a container are
 * collected with the container and need no help, which is why
 * initBackgroundVideo returns nothing and is none the worse for it.
 */
import { swup } from './swup.js'
import { initNav } from './nav.js'
import { initFilter } from './filter.js'
import { initBackgroundVideo } from './backgroundVideo.js'
import { initReveal } from './reveal.js'
import { initMasonry } from './masonry.js'
import { initLightbox } from './lightbox.js'
import { initAnalytics } from './analytics.js'
import { initWordmark } from './wordmark.js'

const MODULES = [
  initNav,
  initFilter,
  initBackgroundVideo,
  initReveal,
  initMasonry,
  initLightbox
]

let teardowns = []

function mount () {
  teardowns = MODULES.map((init) => init()).filter((fn) => typeof fn === 'function')
}

function unmount () {
  teardowns.forEach((teardown) => teardown())
  teardowns = []
}

/* Once, and deliberately outside the registry: it is one delegated listener on
   the document, so it survives every swap, and re-running it would only add a
   second. Pageviews are not reported from here either -- Plausible's tag
   captures History API navigations itself. */
initAnalytics()

/* Also once, and outside the registry for the other half of the same reason:
   `header` IS a swup container, so the bar is replaced on every navigation and
   a module in MODULES would cut the slice again on all eighteen pages. E47
   § 3.5 is an entrance, not a page decoration. */
initWordmark()

mount()

/* `before`, so the modules are taken down while their markup is still in the
   document. A teardown that runs afterwards is reaching into a detached tree:
   nav.js would not find the siblings whose `inert` flag it has to clear. */
swup.hooks.before('content:replace', unmount)
swup.hooks.on('page:view', mount)
